import { parseColumnId } from '@moment/sorm-client-data-access/column-id'

import { GROUP_COUNT_ALIAS } from '@/shared/sorm/grouping-constants'

const CLIENT_COLUMN_ID_SEPARATOR = '.'

interface ToClientColumnIdArgs {
  entity: string
  field: string
  rootEntity: string
}

interface GetRowValueForClientColumnIdArgs {
  row: Record<string, unknown>
  columnId: string
  rootEntity: string
}

export function isCrossEntityClientColumnId(columnId: string): boolean {
  return columnId.indexOf(CLIENT_COLUMN_ID_SEPARATOR) > 0
}

export const parseClientColumnId = parseColumnId

export function toClientColumnId({
  entity,
  field,
  rootEntity,
}: ToClientColumnIdArgs): string {
  if (entity === rootEntity) {
    return field
  }

  return `${entity}${CLIENT_COLUMN_ID_SEPARATOR}${field}`
}

function resolveRowKeyForClientColumnId({
  row,
  columnId,
  rootEntity,
}: GetRowValueForClientColumnIdArgs): string | undefined {
  if (columnId in row) {
    return columnId
  }

  const { entity, field } = parseClientColumnId({ columnId, rootEntity })

  const entityField = `${entity}.${field}`
  if (entityField in row) {
    return entityField
  }

  // SORM may alias conflicting fields (e.g. multiple entities with `id`) to `Entity_field` keys.
  // Prefer this before bare `field` so we do not read the wrong entity's `id`.
  const disambiguatedKey = `${entity}_${field}`
  if (disambiguatedKey in row) {
    return disambiguatedKey
  }

  const rootEntityField = `${rootEntity}.${field}`
  if (rootEntityField in row) {
    return rootEntityField
  }

  // Joined `Entity.id` must not fall back to bare `id` when `entity !== rootEntity` — bare
  // `id` is the root row primary key (e.g. Transaction.id, Lot.id), not joined `Security.id`,
  // `Account.id`, etc.
  const isAmbiguousJoinedId = entity !== rootEntity && field === 'id'
  if (!isAmbiguousJoinedId && field in row) {
    return field
  }

  return undefined
}

/** Whether the row carries the column at all — a fetched null is present, an unrequested field is not. */
export function hasRowValueForClientColumnId(
  args: GetRowValueForClientColumnIdArgs,
): boolean {
  return resolveRowKeyForClientColumnId(args) !== undefined
}

export function getRowValueForClientColumnId(
  args: GetRowValueForClientColumnIdArgs,
): unknown {
  const key = resolveRowKeyForClientColumnId(args)
  return key === undefined ? undefined : args.row[key]
}

interface ApplySormColumnMetadataAliasesArgs {
  row: Record<string, unknown>
  rootEntity: string
  columns: readonly { entity: string; field: string; key: string }[] | undefined
}

/**
 * Maps each SORM response row key from `metadata.columns` onto the client column id
 * (`field` for root entity, `Entity.field` for joins) so value getters and row ids match the grid.
 */
export function applySormColumnMetadataAliases({
  row,
  rootEntity,
  columns,
}: ApplySormColumnMetadataAliasesArgs): Record<string, unknown> {
  if (!columns?.length) {
    return row
  }

  const out: Record<string, unknown> = { ...row }
  for (const col of columns) {
    // Aliased off the root key so the leaf count is not remapped onto `id`.
    if (col.key === GROUP_COUNT_ALIAS) {
      continue
    }
    if (!(col.key in row)) {
      continue
    }
    const clientKey = toClientColumnId({
      entity: col.entity,
      field: col.field,
      rootEntity,
    })
    if (clientKey !== col.key) {
      out[clientKey] = row[col.key]
    }
  }
  return out
}
